import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type MarketVehicleView, type MarketVehiclesView } from '@vello/api-client';
import { REVIEW_NOTE_MAX } from '@vello/driver-eligibility';
import {
  ADMIN_INSPECTION_VEHICLE_CLASSES,
  DRIVER_VEHICLE_STATUSES,
  VEHICLE_INSPECTION_RESULTS,
  cashHandoverActor,
  driverEnablementAuthority,
  emptyInspectionDraft,
  formatMarketInstant,
  inspectionDraftReady,
  inspectionReasonOptions,
  loadMarketVehicles,
  marketTimeZone,
  recordVehicleInspection,
  toggleInspectionCategory,
  vehicleInspectionInput,
  vehicleStatusTone,
  withInspectionResult,
  type InspectionDraft,
  type VehicleInspectionResult,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { MarketField } from '../components/market-field';
import { DrawerStack } from '../components/drawer-stack';
import { usePersonNames } from '../components/person-name';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, FormGrid, EmptyState, Ink, Text, Row, Stack } from '@vello/web-theme';

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string };

/**
 * **Motoristas › Veículos** (W8-DRIVER-ELIGIBILITY, D437, ADR-0069) — the vehicles the market's drivers registered in the
 * app and their operator inspection, over `GET /v1/admin/driver-vehicles` (`vehicle:view`, GEO) and
 * `POST /v1/admin/driver-vehicles/:id/inspections` (`vehicle:manage`, GEO — a CITY grant of the market inspects).
 *
 * - The **market** comes from the operator's scope (never typed); the list is filtered by status (awaiting inspection by
 *   default) and shows the plate, type, year, make and model, the status, and the latest inspection (result, categories,
 *   validity, who, when — in the market's zone).
 * - The **inspection** (the screen's one primary action): passed or failed, the categories the vehicle may serve ticked
 *   FROM the market catalog's list (never typed), the plate as read on the vehicle, a reason from the reason catalog (a
 *   note for `OTHER`) and an optional validity. A pass makes the vehicle ACTIVE for those categories. Every refusal
 *   (`PLATE_MISMATCH`, `CATEGORY_NOT_IN_CATALOG`, …) is explained in words. Whoever lacks `vehicle:manage` sees no write
 *   action. A refused read is an error, never an empty list. No native dialog is ever opened.
 */
export function DriverVehiclesScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const operatorId = me?.id;
  const { mayInspect } = driverEnablementAuthority(grants);
  const [market, setMarket] = useState('');
  const [status, setStatus] = useState<string>('PENDING_INSPECTION');
  const [loaded, setLoaded] = useState<{ readonly market: string; readonly status: string } | undefined>(undefined);
  const [view, setView] = useState<MarketVehiclesView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [selected, setSelected] = useState<MarketVehicleView | undefined>(undefined);
  const [draft, setDraft] = useState<InspectionDraft>(emptyInspectionDraft());
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const refusal = (e: { reasonCode?: string; detail?: string; code: string }, fallbackKey: string): string => {
    return refusalOf('refusal.admin.driver_enablement', e) ?? t(fallbackKey, { code: fail(e) });
  };

  const load = useCallback(
    async (m: string, s: string, background = false): Promise<void> => {
      const n = ++seq.current;
      // A background tick keeps the rows on screen; a new market / status starts from a clean list.
      if (!background) {
        setError(undefined);
        setLoaded({ market: m, status: s });
        setView(undefined);
      }
      if (m === '') return;
      const res = await loadMarketVehicles(runtime, m, s);
      if (n !== seq.current) return;
      if (res.ok) {
        setError(undefined);
        setView(res.value);
      } else setError(refusal(res.error, 'denable.veh.error'));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, t],
  );

  // The list follows the scoped market and the status filter by itself and stays current in the background.
  useEffect(() => {
    setSelected(undefined);
    void load(market, status);
  }, [market, status, load]);
  useAutoRefresh(() => load(market, status, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(loaded?.market, markets);
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const validity = (instant: string | undefined): string => (instant === undefined ? '—' : formatMarketInstant(instant, tz, lang, 'date'));
  const personName = usePersonNames((view?.vehicles ?? []).map((x) => x.inspection?.inspectedBy));
  const who = (id: string | undefined): string => {
    const actor = cashHandoverActor(id ?? null, undefined, operatorId);
    return actor === undefined ? '—' : actor.kind === 'you' ? t('denable.actor.you') : (personName(id) ?? actor.text);
  };
  const statusLabel = (s: string): string => v('vehicles.vehicle_status', s);
  const classLabel = (c: string): string => v('kyc.inspection_vehicle_class', c);

  const open = (v: MarketVehicleView): void => {
    setSelected(v);
    setDraft(emptyInspectionDraft(v.vehicle));
    setMsg(undefined);
  };

  const inspect = async (): Promise<void> => {
    if (selected === undefined || loaded === undefined || !inspectionDraftReady(draft)) return;
    setBusy(true);
    setMsg(undefined);
    const res = await recordVehicleInspection(runtime, selected.vehicle.id, vehicleInspectionInput(loaded.market, draft, tz));
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error, 'ui.actionFailed') });
      return;
    }
    setMsg({
      tone: res.value.inspection.result === 'PASSED' ? 'success' : 'warning',
      text: t(res.value.inspection.result === 'PASSED' ? 'denable.veh.passed' : 'denable.veh.failed', { plate: res.value.vehicle.plate, status: statusLabel(res.value.vehicle.status) }),
    });
    setSelected(undefined);
    setDraft(emptyInspectionDraft());
    await load(loaded.market, loaded.status);
  };

  const categories = view?.categories ?? [];

  return (
    <>
      <PageHeader title={t('denable.veh.title')} subtitle={t('denable.veh.subtitle')} />

      <Panel><Stack gap={4}>
        <Ink as="p" tone="secondary">{t('denable.veh.explain')}</Ink>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow columns="minmax(0, 1fr) minmax(0, 240px)">
            <MarketField id="veh-market" value={market} onChange={setMarket} />
            <Field label={t('denable.veh.status')} htmlFor="veh-status">
              <Select id="veh-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {DRIVER_VEHICLE_STATUSES.map((s) => (
                  <option key={s} value={s}>{statusLabel(s)}</option>
                ))}
                <option value="">{t('denable.veh.status.all')}</option>
              </Select>
            </Field>
          </FormRow>
        </form>
        {loaded !== undefined && loaded.market === '' && <Notice tone="neutral">{t('denable.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {!mayInspect && <Notice tone="neutral">{t('denable.veh.noInspect')}</Notice>}
      </Stack></Panel>

      {msg !== undefined && selected === undefined && (
        <div>
          <Notice tone={msg.tone}>{msg.text}</Notice>
        </div>
      )}

      <section aria-label={t('denable.veh.list')}>
        <Panel padded={false}>
          <Table
            columns={[
              { key: 'plate', header: t('denable.veh.col.plate'), render: (veh: MarketVehicleView) => <span className="tnum">{veh.vehicle.plate}</span> },
              {
                key: 'vehicle',
                header: t('denable.veh.col.vehicle'),
                render: (veh: MarketVehicleView) => (
                  <span>
                    {`${veh.vehicle.make} ${veh.vehicle.model}`}
                    <Text role="caption" tone="tertiary" block>{t('denable.veh.typeYear', { type: v('service_categories.vehicle_class', veh.vehicle.type), year: String(veh.vehicle.year) })}</Text>
                  </span>
                ),
              },
              { key: 'status', header: t('denable.veh.col.status'), render: (veh: MarketVehicleView) => <Badge tone={vehicleStatusTone(veh.vehicle.status)}>{statusLabel(veh.vehicle.status)}</Badge> },
              {
                key: 'inspection',
                header: t('denable.veh.col.inspection'),
                render: (veh: MarketVehicleView) =>
                  veh.inspection === undefined ? (
                    <Text role="caption" tone="tertiary" block>{t('denable.veh.neverInspected')}</Text>
                  ) : (
                    <span>
                      <Badge tone={veh.inspection.result === 'PASSED' ? 'success' : 'danger'}>{v('vehicles.inspection_result', veh.inspection.result)}</Badge>
                      {veh.inspection.vehicleClass !== undefined && <Text role="caption" tone="tertiary" block>{t('denable.veh.classSeen', { class: classLabel(veh.inspection.vehicleClass) })}</Text>}
                      <Text role="caption" tone="tertiary" mono block>
                        {t('denable.veh.inspectionSummary', {
                          categories: veh.inspection.categories.length > 0 ? veh.inspection.categories.map((c) => v('ride.category', c)).join(', ') : '—',
                          validity: validity(veh.inspection.validUntil),
                          by: who(veh.inspection.inspectedBy),
                          at: when(veh.inspection.inspectedAt),
                        })}
                      </Text>
                      <Text role="caption" tone="tertiary" block>{v('kyc.evidence_reason', veh.inspection.reasonCode)}</Text>
                    </span>
                  ),
              },
            ]}
            rows={view?.vehicles ?? []}
            rowKey={(v) => v.vehicle.id}
            {...(mayInspect ? { onRowClick: (veh: MarketVehicleView) => { if (veh.vehicle.status !== 'RETIRED') open(veh); } } : {})}
            {...(selected !== undefined ? { selectedKey: selected.vehicle.id } : {})}
            loading={loaded !== undefined && loaded.market !== '' && view === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void load(market, status)}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={loaded === undefined || loaded.market === '' ? t('denable.marketHint') : t('denable.veh.empty')} />}
          />
        </Panel>
      </section>

      <Drawer open={mayInspect && selected !== undefined} onClose={() => setSelected(undefined)} labelledBy="veh-form-title">
        {mayInspect && selected !== undefined && (
          <>
            <DrawerHeader titleId="veh-form-title" title={t('denable.veh.form.title', { plate: selected.vehicle.plate })} onClose={() => setSelected(undefined)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
              {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
              <Ink as="p" tone="secondary">{t('denable.veh.form.hint')}</Ink>
              <FormGrid>
                <Field label={t('denable.veh.result')} htmlFor="veh-result">
                  <Select id="veh-result" value={draft.result} onChange={(e) => setDraft(withInspectionResult(draft, e.target.value as VehicleInspectionResult))}>
                    {VEHICLE_INSPECTION_RESULTS.map((r) => (
                      <option key={r} value={r}>{v('vehicles.inspection_result', r)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t(draft.result === 'PASSED' ? 'denable.veh.class' : 'denable.veh.classOptional')} htmlFor="veh-class" hint={t('denable.veh.class.hint', { typed: v('service_categories.vehicle_class', selected.vehicle.type) })}>
                  <Select id="veh-class" value={draft.vehicleClass} onChange={(e) => setDraft({ ...draft, vehicleClass: e.target.value })}>
                    <option value="">{t('denable.veh.class.choose')}</option>
                    {ADMIN_INSPECTION_VEHICLE_CLASSES.map((c) => (
                      <option key={c} value={c}>{classLabel(c)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('denable.veh.plateRead')} htmlFor="veh-plate" hint={t('denable.veh.plateRead.hint')}>
                  <TextInput id="veh-plate" className="tnum" value={draft.plate} autoComplete="off" spellCheck={false} onChange={(e) => setDraft({ ...draft, plate: e.target.value })} />
                </Field>
                <Field label={t('denable.docs.reason')} htmlFor="veh-reason">
                  <Select id="veh-reason" value={draft.reasonCode} onChange={(e) => setDraft({ ...draft, reasonCode: e.target.value })}>
                    {inspectionReasonOptions(draft.result).map((r) => (
                      <option key={r} value={r}>{v('kyc.evidence_reason', r)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('denable.veh.validUntil')} htmlFor="veh-valid" hint={t('denable.veh.validUntil.hint')}>
                  <TextInput id="veh-valid" className="tnum" type="date" value={draft.validUntilDate} onChange={(e) => setDraft({ ...draft, validUntilDate: e.target.value })} />
                </Field>
                <Field label={t(draft.reasonCode === 'OTHER' ? 'denable.docs.noteRequired' : 'denable.docs.note')} htmlFor="veh-note">
                  <TextInput id="veh-note" value={draft.note} maxLength={REVIEW_NOTE_MAX} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
                </Field>
              </FormGrid>
              {draft.result === 'PASSED' && (
                <Stack as="fieldset" gap={2}>
                  <Text as="legend" role="label" tone="secondary">{t('denable.veh.categories')}</Text>
                  {categories.length === 0 ? (
                    <Notice tone="warning">{t('denable.veh.noCategories')}</Notice>
                  ) : (
                    <Row gap={4} align="stretch" wrap>
                      {categories.map((c) => (
                        <CheckRow key={c.category} id={`veh-cat-${c.category}`} checked={draft.categories.includes(c.category)} onChange={(on) => setDraft(toggleInspectionCategory(draft, c.category, on))}>
                          <span>{v('ride.category', c.category)}</span>
                        </CheckRow>
                      ))}
                    </Row>
                  )}
                </Stack>
              )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setSelected(undefined)}>{t('denable.editor.cancel')}</Button>
              <Button variant="primary" disabled={busy || !inspectionDraftReady(draft)} onClick={() => void inspect()}>{t('denable.veh.submit')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
